import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { DataTable, LoadingSpinner, SearchInput, PageHeader, useDataTableUrl } from '@airclou/ui'
import { getServiceUrl, formatDateInTz } from '@airclou/utils'
import { Layers, ArrowRight, Building2 } from 'lucide-react'
import { ErrorState } from '../../components/state/ErrorState'
import { listWorkspaces, type Workspace } from '../../api/workspaces'
import { useTimezone } from '@airclou/shell/timezone'

/** Workspace logo when one is set, otherwise the generic workspace icon. */
function WorkspaceLogo({ workspace, size }: { workspace: Workspace; size: 'sm' | 'md' }) {
  const { t } = useTranslation('dashboard')
  // Remember which src failed rather than a bare "failed" flag: DataTable reuses cell
  // component instances across rows when sorting, so a boolean would stick to the wrong
  // workspace. Comparing against the current src self-corrects on reuse.
  const [failedSrc, setFailedSrc] = useState<string | null>(null)

  const box = size === 'sm' ? 'w-10 h-10' : 'w-12 h-12'
  const icon = size === 'sm' ? 'w-5 h-5' : 'w-6 h-6'

  const src = workspace.logo_url
    ? workspace.logo_url.startsWith('http')
      ? workspace.logo_url
      : `${getServiceUrl('dashboard')}${workspace.logo_url}`
    : null
  const showLogo = src !== null && src !== failedSrc

  return (
    // The brand tint backs the icon fallback only. Behind a real logo it would show as
    // letterbox bars beside a non-square wordmark, or wash through a transparent PNG.
    <div
      className={`flex items-center justify-center ${box} flex-shrink-0 rounded-lg overflow-hidden ${
        showLogo ? '' : 'bg-primary/15 dark:bg-primary/25'
      }`}
    >
      {showLogo ? (
        <img
          src={src}
          alt={t('workspaces.logoAlt', { name: workspace.name })}
          className="w-full h-full object-contain"
          // Uploads are stored at full resolution (5MB cap, no server-side resize), so a
          // long list would otherwise pull every original just to fill a 48px tile.
          loading="lazy"
          onError={() => setFailedSrc(src)}
        />
      ) : (
        <Layers className={`${icon} text-primary`} />
      )}
    </div>
  )
}

export function WorkspacesPage() {
  const timezone = useTimezone()
  const { t } = useTranslation('dashboard')
  const [workspaces, setWorkspaces] = useState<Workspace[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Search and sort live in the URL (same pattern as the audit log), so they
  // survive a reload, a shared link and back/forward.
  const { search: searchQuery, onSearchChange, sorting, onSortingChange } = useDataTableUrl()

  // Fetch workspaces from API
  const loadWorkspaces = async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await listWorkspaces()
      setWorkspaces(response.workspaces)
    } catch (err: any) {
      setError(err.message || t('workspaces.failedToLoadWorkspaces'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadWorkspaces()
  }, [])

  // Filter workspaces by search
  const filteredWorkspaces = workspaces.filter(ws =>
    ws.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (ws.company_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    ws.slug.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const columns: any[] = [
    {
      id: 'name',
      header: t('workspaces.workspace'),
      cell: (workspace: Workspace) => (
        <div className="flex items-center gap-3">
          <WorkspaceLogo workspace={workspace} size="sm" />
          <div>
            <div className="font-medium text-foreground">{workspace.name}</div>
            <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
              <Building2 className="w-3 h-3" />
              {workspace.company_name || workspace.slug}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'slug',
      header: t('common.slug'),
      cell: (workspace: Workspace) => (
        <div className="text-sm text-gray-600 dark:text-gray-400 font-mono">
          {workspace.slug}
        </div>
      ),
    },
    {
      id: 'created',
      header: t('common.created'),
      cell: (workspace: Workspace) => (
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {formatDateInTz(workspace.created_at, timezone)}
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader title={t('workspaces.title')} description={t('workspaces.subtitle')} />

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="flex-1 max-w-sm">
          <SearchInput
            value={searchQuery}
            onChange={onSearchChange}
            placeholder={t('workspaces.searchWorkspaces')}
          />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : error ? (
        <ErrorState
          title={t('workspaces.failedToLoadWorkspaces')}
          description={error}
          onRetry={loadWorkspaces}
        />
      ) : (
        <>
          {/* Quick Access Cards */}
          {filteredWorkspaces.length > 0 && filteredWorkspaces.length <= 6 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredWorkspaces.map(workspace => (
                <Link
                  key={workspace.id}
                  to={`/workspaces/${workspace.id}`}
                  className="flex items-center gap-4 p-4 bg-card rounded-lg border border-border hover:border-primary/40 dark:hover:border-primary hover:shadow-md transition-all text-left"
                >
                  <WorkspaceLogo workspace={workspace} size="md" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground truncate">
                      {workspace.name}
                    </p>
                    <p className="text-sm text-muted-foreground truncate">
                      {workspace.company_name || workspace.slug}
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-muted-foreground" />
                </Link>
              ))}
            </div>
          )}

          {/* All Workspaces Table */}
          {filteredWorkspaces.length > 6 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold text-foreground">
                {t('workspaces.allWorkspaces')}
              </h2>
              <DataTable
                data={filteredWorkspaces}
                columns={columns}
                sorting={sorting}
                onSortingChange={onSortingChange}
                getRowHref={(workspace) => `/workspaces/${workspace.id}`}
                emptyMessage={t('workspaces.noWorkspacesFound')}
              />
            </div>
          )}

          {/* Empty State */}
          {filteredWorkspaces.length === 0 && (
            <div className="text-center py-12">
              <Layers className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-semibold text-foreground">{t('workspaces.noWorkspaces')}</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {searchQuery ? t('workspaces.noWorkspacesSearch') : t('workspaces.noWorkspacesCreated')}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  )
}
